import { createFileRoute, Link } from "@tanstack/react-router";
import { Header, Footer } from "@/components/layout";
import { PolandMapLazy } from "@/components/poland-map-lazy";
import { buildMeta } from "@/lib/seo";
import { TOP_DEPARTURE_CITIES, TOP_DESTINATIONS, THEMES, cityFromGen, cityToGen, countryGen, departureCityLabel, fixPolishGrammar } from "@/lib/seo-routes";
import { findAirport, allCountries, countrySlug } from "@/lib/airports";
import { Plane, MapPin, Globe, Sparkles, TrendingUp, ArrowRight } from "lucide-react";
import { breadcrumbScript } from "@/lib/breadcrumbs";

// Najpopularniejsze trasy z Polski — kierują DO WYNIKÓW (nie do landingu),
// czyli wprost do wyszukiwarki z gotowymi parametrami: a=1 dorosły, RT, ekonomia.
const TOP_ROUTES: { from: string; fromCity: string; to: string; toCity: string; price: string }[] = [
  { from: "WAW", fromCity: "Warszawa", to: "LGW", toCity: "Londyn",      price: "sprawdź cenę" },
  { from: "KRK", fromCity: "Kraków",   to: "STN", toCity: "Londyn",      price: "sprawdź cenę" },
  { from: "WAW", fromCity: "Warszawa", to: "BCN", toCity: "Barcelona",   price: "sprawdź cenę" },
  { from: "KTW", fromCity: "Katowice", to: "AYT", toCity: "Antalya",     price: "sprawdź cenę" },
  { from: "WAW", fromCity: "Warszawa", to: "FCO", toCity: "Rzym",        price: "sprawdź cenę" },
  { from: "GDN", fromCity: "Gdańsk",   to: "ATH", toCity: "Ateny",       price: "sprawdź cenę" },
  { from: "WRO", fromCity: "Wrocław",  to: "AGP", toCity: "Malaga",      price: "sprawdź cenę" },
  { from: "POZ", fromCity: "Poznań",   to: "STN", toCity: "Londyn",      price: "sprawdź cenę" },
  { from: "WAW", fromCity: "Warszawa", to: "DXB", toCity: "Dubaj",       price: "sprawdź cenę" },
  { from: "KRK", fromCity: "Kraków",   to: "PMI", toCity: "Palma",       price: "sprawdź cenę" },
  { from: "WAW", fromCity: "Warszawa", to: "CDG", toCity: "Paryż",       price: "sprawdź cenę" },
  { from: "KTW", fromCity: "Katowice", to: "HRG", toCity: "Hurghada",    price: "sprawdź cenę" },
];

export const Route = createFileRoute("/loty/")({
  head: () => {
    const base = buildMeta({
      title: "Tanie loty z Polski 2026 — porównaj ceny biletów",
      // ≤160 znaków: skrócony opis, ten sam zestaw lotnisk wylotu.
      description: "Wybierz lotnisko wylotu w Polsce, kraj lub miasto docelowe. Porównaj dostępne połączenia w Aviasales dla wybranych dat.",
      path: "/loty",
    });
    const departures = TOP_DEPARTURE_CITIES.map((c) => findAirport(c)).filter((a): a is NonNullable<typeof a> => !!a);
    const itemList = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Tanie loty z polskich miast",
      itemListElement: departures.map((a, i) => ({
        "@type": "ListItem",
        position: i + 1,
        url: `https://tanielatanie.pl/loty/${a.code.toLowerCase()}`,
        name: fixPolishGrammar(`Tanie loty z ${departureCityLabel(a.code)}`),
      })),
    };
    const breadcrumb = breadcrumbScript([
      { name: "Strona główna", path: "/" },
      { name: "Loty", path: "/loty" },
    ]);
    return { ...base, scripts: [{ type: "application/ld+json", children: JSON.stringify(itemList) }, breadcrumb] };
  },
  component: LotyHub,
});

function LotyHub() {
  const departures = TOP_DEPARTURE_CITIES.map((c) => findAirport(c)).filter((a): a is NonNullable<typeof a> => !!a);
  const destinations = TOP_DESTINATIONS.map((c) => findAirport(c)).filter((a): a is NonNullable<typeof a> => !!a);
  const countries = allCountries().slice(0, 60);

  return (
    <div className="min-h-screen">
      <Header />
      <main className="container mx-auto max-w-6xl px-4 py-10">
        <Link to="/tanie-loty" className="mb-4 inline-block text-sm text-primary hover:underline">Tanie loty — wyszukiwarka</Link>
        <header className="mb-10">
          <h1 className="font-display text-4xl font-bold leading-tight md:text-5xl">
            Tanie loty z Polski — wszystkie kierunki
          </h1>
          <p className="mt-3 max-w-2xl text-lg text-muted-foreground">
            Wybierz miasto wylotu, popularny kierunek, kraj lub temat, żeby znaleźć najtańsze loty z polskich lotnisk.
          </p>
        </header>

        <div className="mb-10">
          <PolandMapLazy />
        </div>

        <Block icon={<TrendingUp className="h-5 w-5 text-primary" />} title="Najpopularniejsze trasy z Polski">
          <p className="mb-4 -mt-2 text-sm text-muted-foreground">
            Wybierz trasę, a następnie daty podróży, aby sprawdzić dostępne loty i ceny.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {TOP_ROUTES.map((r) => {
              return (
                <Link
                  key={`${r.from}-${r.to}`}
                  to="/loty/$from/$to"
                  params={{ from: r.from.toLowerCase(), to: r.to.toLowerCase() }}
                  aria-label={fixPolishGrammar(`Wyszukaj loty z ${cityFromGen(r.fromCity)} do ${cityToGen(r.toCity)}`)}
                  className="glass group flex items-center justify-between rounded-xl p-4 transition-all hover:shadow-glow"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                      {r.from} → {r.to}
                    </div>
                    <div className="truncate font-display text-base font-semibold group-hover:text-primary">
                      {r.fromCity} → {r.toCity}
                    </div>
                    <div className="text-xs text-muted-foreground">{r.price}</div>
                  </div>
                  <ArrowRight className="h-4 w-4 shrink-0 text-primary opacity-60 transition-transform group-hover:translate-x-1 group-hover:opacity-100" />
                </Link>
              );
            })}
          </div>
        </Block>

        <Block icon={<Plane className="h-5 w-5 text-primary" />} title="Lotniska wylotu w Polsce">
          <Grid>
            {departures.map((a) => (
              <Tile key={a.code} href={`/loty/${a.code.toLowerCase()}`}>
                Tanie loty z {departureCityLabel(a.code)}
              </Tile>
            ))}
          </Grid>
        </Block>

        <Block icon={<MapPin className="h-5 w-5 text-primary" />} title="Popularne kierunki">
          <Grid>
            {destinations.slice(0, 36).map((a) => (
              <Tile key={a.code} href={`/loty/waw/${a.code.toLowerCase()}`}>
                Tanie loty do {cityToGen(a.city)}
              </Tile>
            ))}
          </Grid>
        </Block>

        <Block icon={<Globe className="h-5 w-5 text-primary" />} title="Loty wg kraju">
          <Grid>
            {countries.map((c) => (
              <Tile key={c} href={`/loty/kraj/${countrySlug(c)}`}>
                Loty do {countryGen(c)}
              </Tile>
            ))}
          </Grid>
        </Block>

        <Block icon={<Sparkles className="h-5 w-5 text-primary" />} title="Tematy">
          <Grid>
            {THEMES.map((t) => (
              <Tile key={t.slug} href={`/loty/temat/${t.slug}`}>
                {t.label}
              </Tile>
            ))}
          </Grid>
        </Block>
      </main>
      <Footer />
    </div>
  );
}

function Block({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="mb-4 inline-flex items-center gap-2 font-display text-2xl font-bold">{icon}{title}</h2>
      {children}
    </section>
  );
}

function Grid({ children }: { children: React.ReactNode }) {
  return <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">{children}</div>;
}

function Tile({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      to={href as string}
      className="rounded-lg border border-border/50 bg-card/40 px-3 py-2.5 text-sm transition hover:border-primary/40 hover:bg-card/70"
    >
      {children}
    </Link>
  );
}
